import React, { useState, useEffect, useRef, useCallback } from "react";

import Modal from "components/Modal";
import { fetchPublicAPI } from "hooks/usePublicAPI";
import { formatPhoneNumberForAPI } from "helpers/phone";

type ValidationCache = { [key: string]: boolean };

type ChangeContactModalProps = {
  isOpen: boolean;
  onClose: () => void;
  type: "email" | "phone" | null;
  onSubmit: (newValue: string, type: "email" | "phone") => void;
  isLoading: boolean;
  error: string | null;
};

const ChangeContactModal: React.FC<ChangeContactModalProps> = ({
  isOpen,
  onClose,
  type,
  onSubmit,
  isLoading,
  error,
}) => {
  const [value, setValue] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false); // Track focus state
  const [isTouched, setIsTouched] = useState(false); // Track if blurred at least once

  // Refs for debouncing and caching validation
  const validationTimeoutRef = useRef<number | null>(null);
  const validationCacheRef = useRef<ValidationCache>({});

  // Debounced validation function (adapted from PhoneNumberField)
  const validatePhoneNumber = useCallback(
    async (number: string) => {
      if (!number && type === "phone") {
        setValidationError("Phone number is required.");
        return;
      }
      if (!number) return;

      const formattedNumber = formatPhoneNumberForAPI(number);

      if (validationCacheRef.current[formattedNumber] !== undefined) {
        setValidationError(
          validationCacheRef.current[formattedNumber]
            ? null
            : "Phone number is invalid.",
        );
        setIsValidating(false);
        return;
      }

      setIsValidating(true);
      setValidationError(null);

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      validationTimeoutRef.current = window.setTimeout(async () => {
        try {
          const data = await fetchPublicAPI(
            `/validation/phones/${formattedNumber}`,
          );
          validationCacheRef.current[formattedNumber] = data.valid;
          setValidationError(data.valid ? null : "Phone number is invalid.");
        } catch (err) {
          console.error("Error validating phone number", err);
          // Treat as valid if API fails to avoid blocking user
          setValidationError(null);
        } finally {
          setIsValidating(false);
        }
      }, 300); // 300ms debounce
    },
    [type],
  );

  // Synchronous validation for email
  const validateEmail = useCallback((email: string) => {
    const emailPattern = /^[\S@]+@[\S@]+\.[\S@]+$/; // Regex for basic email structure

    if (!email) {
      setValidationError("Email is required.");
    } else if (!emailPattern.test(email)) {
      setValidationError("Please enter a valid email address.");
    } else {
      setValidationError(null); // Clear error if valid
    }
  }, []);

  // Clear state and validation when modal opens or type changes
  useEffect(() => {
    if (isOpen) {
      setValue("");
      setValidationError(null);
      setIsValidating(false);
      setIsFocused(false); // Reset focus/touched state
      setIsTouched(false);
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    } else {
      // Clear cache when modal is closed
      validationCacheRef.current = {};
    }
  }, [isOpen, type]);

  // Trigger validation when value changes
  useEffect(() => {
    if (type === "phone") {
      validatePhoneNumber(value);
    } else if (type === "email") {
      validateEmail(value);
    }
  }, [value, type, validatePhoneNumber, validateEmail]);

  // Handle input change - basic cleaning for phone
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    if (type === "phone") {
      // Allow digits, +, -, (, ), spaces for easier input, but validation uses cleaned number
      inputValue = inputValue.replace(/[^\d\s()+-]/g, "");
    }
    setValue(inputValue);

    // Clear general API error when user starts typing again
    if (error) {
      // TODO: Need a way to clear the parent's error state
      // This might require lifting error state up or passing a clearError callback
      console.warn("Cannot clear parent error state from modal");
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!type || isValidating || validationError) return;

    // Format phone number before submitting
    const valueToSubmit =
      type === "phone" ? formatPhoneNumberForAPI(value) : value;
    onSubmit(valueToSubmit, type);
  };

  const inputType = type === "email" ? "email" : "tel";
  const placeholder =
    type === "email" ? "new.email@example.com" : "555 123 4567";
  const title = `Change ${type === "email" ? "Email Address" : "Phone Number"}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Enter the new {type === "email" ? "email address" : "phone number"}{" "}
          you would like to use. We'll send a verification{" "}
          {type === "email" ? "link" : "code"} to confirm.
        </p>
        <input
          key={`change-input-${type}`} // Keep key for focus stability
          type={inputType}
          autoComplete={type === "email" ? "email" : "tel"}
          value={value}
          onFocus={() => setIsFocused(true)} // Set focus true
          onBlur={() => {
            setIsFocused(false);
            setIsTouched(true); // Mark as touched on blur
          }}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-lg font-medium text-soil transition-colors placeholder:text-oat-700 focus:border-cantelope focus:shadow-focus focus:outline-none"
          required
          autoFocus // Focus input when modal opens
        />
        {/* Calculate showError based on state */}
        {(() => {
          const showError = isTouched && !isFocused && !!validationError;
          return (
            <>
              {" "}
              {/* Use fragment to avoid adding extra div */}
              {/* Display validation error only if showError is true*/}
              {showError && (
                <p className="text-sm text-red-600">{validationError}</p>
              )}
              {/* Display general API error from parent (always show if present) */}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </>
          );
        })()}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cantelope focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            // Disable if loading, validating (phone), empty value, or validation error
            disabled={
              isLoading ||
              (type === "phone" && isValidating) ||
              !value || // Disable if input is empty
              !!validationError // Disable if there's any validation error
            }
            className="inline-flex justify-center rounded border border-transparent bg-cantelope px-4 py-2 text-sm font-medium text-soil shadow-sm hover:bg-cantelope/80 focus:outline-none focus:ring-2 focus:ring-cantelope focus:ring-offset-2 disabled:opacity-50"
          >
            {isLoading
              ? "Sending..."
              : type === "phone" && isValidating
                ? "Validating..."
                : "Send Verification"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangeContactModal;
